import React, { useState, useContext } from 'react';
import {
	Box,
	Card,
	CardContent,
	Button,
	TextField,
	Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from '@mui/icons-material/Add';
import ReFreshIcon from '@mui/icons-material/Refresh';
import { useSnackbar } from 'notistack';
import CreateBidModal from '../../../../Components/Modals/CreateBid';
import ChangeOrderModal from '../../../../Components/Modals/ChangeOrderModal';
import SendInvoiceModal from '../../../../Components/Modals/SendInvoiceModal';
import RecordRefundModal from '../../../../Components/Modals/RecordRefundModal';
import Modal from '../../../../Components/Modals/Modal';
import { makeApiRequest } from '../../../../Shared/Api';
import { refreshOnboardingChecklist } from '../../../../Shared/onboarding';
import { JobDetailContext } from '../../context/JobDetailContext';
import { toDatetimeLocalValue } from '../../../../Shared/datetime';
import { BID_STATUS, useBidActions } from '../../../../Shared/bids';

const JobActions = ({ jobId, onJobUpdated }) => {
	const { enqueueSnackbar } = useSnackbar();
	const [isCreateBidModalOpen, setIsCreateBidModalOpen] = useState(false);
	const [isChangeOrderModalOpen, setIsChangeOrderModalOpen] = useState(false);
	const [expirationModalOpen, setExpirationModalOpen] = useState(false);
	const [expiresAtLocal, setExpiresAtLocal] = useState('');
	const [resendLoading, setResendLoading] = useState(false);
	const [invoiceLoading, setInvoiceLoading] = useState(false);
	const [depositResendLoading, setDepositResendLoading] = useState(false);
	const [invoiceResendLoading, setInvoiceResendLoading] = useState(false);
	const [voidInvoiceLoading, setVoidInvoiceLoading] = useState(false);
	const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
	const [refundModalOpen, setRefundModalOpen] = useState(false);
	const [refundLoading, setRefundLoading] = useState(false);

	const { state } = useContext(JobDetailContext);
	const { selectedJob } = state;
	const bid = selectedJob?.bid_details;

	const canRecordRefund = Boolean(
		selectedJob?.refund_summary?.can_record_refund ??
		bid?.capabilities?.refund?.can_record_refund,
	);

	const {
		canResend,
		canSendInvoice,
		canResendDeposit,
		canResendInvoice,
		canVoidInvoice,
		hasSentProposal,
		resendBlocked,
		invoiceBlocked,
		resendDisabled: resendDisabledByRules,
		invoiceDisabled: invoiceDisabledByRules,
		resendDepositDisabled: resendDepositDisabledByRules,
		resendInvoiceDisabled: resendInvoiceDisabledByRules,
		voidInvoiceDisabled: voidInvoiceDisabledByRules,
	} = useBidActions(bid, {
		depositPaidOnJob: selectedJob?.deposit_paid_amount,
	});

	const openExpirationModalWithDefault = () => {
		const d = new Date();
		d.setDate(d.getDate() + 14);
		setExpiresAtLocal(toDatetimeLocalValue(d));
		setExpirationModalOpen(true);
	};

	const postResend = async (body) => {
		setResendLoading(true);
		try {
			await makeApiRequest('POST', `/bids/${bid.id}/resend-proposal/`, body);
			enqueueSnackbar('Proposal email sent', { variant: 'success' });
			setExpirationModalOpen(false);
			if (typeof onJobUpdated === 'function') {
				await onJobUpdated();
			}
			refreshOnboardingChecklist();
		} catch (err) {
			const data = err.data || {};
			if (data.requires_expiration) {
				openExpirationModalWithDefault();
				enqueueSnackbar('Set a new expiration date to resend this bid.', {
					variant: 'info',
				});
			} else {
				enqueueSnackbar(err.message || 'Could not resend proposal', {
					variant: 'error',
				});
			}
		} finally {
			setResendLoading(false);
		}
	};

	const handleResendBid = async () => {
		if (!bid?.id || !canResend) return;
		if (bid.status === BID_STATUS.EXPIRED) {
			openExpirationModalWithDefault();
			return;
		}
		await postResend({});
	};

	const handleSendInvoice = async ({ include_card_fee_notice }) => {
		if (!bid?.id || !canSendInvoice) return;
		setInvoiceLoading(true);
		try {
			await makeApiRequest('POST', `/bids/${bid.id}/send-invoice/`, {
				include_card_fee_notice,
			});
			setInvoiceModalOpen(false);
			if (typeof onJobUpdated === 'function') {
				await onJobUpdated();
			}
		} catch (err) {
			enqueueSnackbar(err.message || 'Could not send invoice', {
				variant: 'error',
			});
		} finally {
			setInvoiceLoading(false);
		}
	};

	const handleResendDeposit = async () => {
		if (!bid?.id || !canResendDeposit) return;
		setDepositResendLoading(true);
		try {
			await makeApiRequest('POST', `/bids/${bid.id}/resend-deposit/`, {});
			enqueueSnackbar('Deposit email sent', { variant: 'success' });
			if (typeof onJobUpdated === 'function') {
				await onJobUpdated();
			}
		} catch (err) {
			enqueueSnackbar(err.message || 'Could not resend deposit email', {
				variant: 'error',
			});
		} finally {
			setDepositResendLoading(false);
		}
	};

	const handleResendInvoice = async () => {
		if (!bid?.id || !canResendInvoice) return;
		setInvoiceResendLoading(true);
		try {
			await makeApiRequest('POST', `/bids/${bid.id}/resend-invoice/`, {});
			enqueueSnackbar('Invoice email sent', { variant: 'success' });
			if (typeof onJobUpdated === 'function') {
				await onJobUpdated();
			}
		} catch (err) {
			enqueueSnackbar(err.message || 'Could not resend invoice email', {
				variant: 'error',
			});
		} finally {
			setInvoiceResendLoading(false);
		}
	};

	const handleVoidInvoice = async () => {
		if (!bid?.id || !canVoidInvoice) return;
		setVoidInvoiceLoading(true);
		try {
			await makeApiRequest('POST', `/bids/${bid.id}/void-invoice/`, {});
			enqueueSnackbar('Invoice voided', { variant: 'success' });
			if (typeof onJobUpdated === 'function') {
				await onJobUpdated();
			}
		} catch (err) {
			enqueueSnackbar(err.message || 'Could not void invoice', {
				variant: 'error',
			});
		} finally {
			setVoidInvoiceLoading(false);
		}
	};

	const handleConfirmExpiration = async () => {
		if (!expiresAtLocal) {
			enqueueSnackbar('Choose an expiration date and time', {
				variant: 'warning',
			});
			return;
		}
		const iso = new Date(expiresAtLocal).toISOString();
		await postResend({ expires_at: iso });
	};

	const handleRecordRefund = async ({ items, reason, notes, bid: bidId }) => {
		setRefundLoading(true);
		try {
			await makeApiRequest('POST', `/jobs/${jobId}/refunds/`, {
				items,
				reason,
				notes,
				bid: bidId,
			});
			setRefundModalOpen(false);
			enqueueSnackbar('Refund recorded', { variant: 'success' });
			if (typeof onJobUpdated === 'function') {
				await onJobUpdated();
			}
		} catch (err) {
			enqueueSnackbar(err.message || 'Could not record refund', {
				variant: 'error',
			});
		} finally {
			setRefundLoading(false);
		}
	};

	const handlePrimaryBidAction = () => {
		if (hasSentProposal) {
			setIsChangeOrderModalOpen(true);
		} else {
			setIsCreateBidModalOpen(true);
		}
	};

	const resendDisabled = resendDisabledByRules || resendLoading;
	const invoiceDisabled = invoiceDisabledByRules || invoiceLoading;
	const resendDepositDisabled =
		resendDepositDisabledByRules || depositResendLoading;
	const resendInvoiceDisabled =
		resendInvoiceDisabledByRules || invoiceResendLoading;
	const voidInvoiceDisabled = voidInvoiceDisabledByRules || voidInvoiceLoading;
	const showResendDeposit = canResendDeposit;
	const showResendInvoice = canResendInvoice;
	const showVoidInvoice = canVoidInvoice;
	const syncBlockMessage = '';

	return (
		<Box>
			<Card elevation={0}>
				<CardContent sx={{ p: 2.5 }}>
					<Button
						variant="outlined"
						color="primary"
						sx={{ width: '100%' }}
						onClick={handlePrimaryBidAction}
						startIcon={hasSentProposal ? <ReFreshIcon /> : <AddIcon />}
					>
						{hasSentProposal ? 'Create Change Order' : 'Create Bid'}
					</Button>
					<Tooltip title={resendBlocked ? syncBlockMessage : ''}>
						<span>
							<Button
								variant="outlined"
								color="primary"
								sx={{ width: '100%', mt: 1, mb: 1 }}
								disabled={resendDisabled}
								onClick={handleResendBid}
							>
								{resendLoading ? 'Sending...' : 'Re-send Bid'}
								<SendIcon sx={{ fontSize: 18, ml: 1 }} />
							</Button>
						</span>
					</Tooltip>
					{showResendDeposit ? (
						<Button
							variant="outlined"
							color="primary"
							sx={{ width: '100%', mb: 1 }}
							disabled={resendDepositDisabled}
							onClick={handleResendDeposit}
						>
							{depositResendLoading ? 'Sending...' : 'Resend Deposit Email'}
							<SendIcon sx={{ fontSize: 18, ml: 1 }} />
						</Button>
					) : null}
					{showResendInvoice ? (
						<Tooltip title={invoiceBlocked ? syncBlockMessage : ''}>
							<span>
								<Button
									variant="outlined"
									color="primary"
									sx={{ width: '100%', mb: 1 }}
									disabled={resendInvoiceDisabled}
									onClick={handleResendInvoice}
								>
									{invoiceResendLoading ? 'Sending...' : 'Resend Invoice'}
									<SendIcon sx={{ fontSize: 18, ml: 1 }} />
								</Button>
							</span>
						</Tooltip>
					) : null}
					{showVoidInvoice ? (
						<Button
							variant="outlined"
							color="warning"
							sx={{ width: '100%', mb: 1 }}
							disabled={voidInvoiceDisabled}
							onClick={handleVoidInvoice}
						>
							{voidInvoiceLoading ? 'Voiding...' : 'Void Invoice'}
						</Button>
					) : null}
					<Tooltip title={invoiceBlocked ? syncBlockMessage : ''}>
						<span>
							<Button
								variant="outlined"
								color="primary"
								sx={{ width: '100%' }}
								disabled={invoiceDisabled}
								onClick={() => setInvoiceModalOpen(true)}
							>
								{invoiceLoading ? 'Sending...' : 'Send Invoice'}
								<SendIcon sx={{ fontSize: 18, ml: 1 }} />
							</Button>
						</span>
					</Tooltip>
					{canRecordRefund ? (
						<Button
							variant="outlined"
							color="warning"
							sx={{ width: '100%', mt: 1 }}
							disabled={refundLoading}
							onClick={() => setRefundModalOpen(true)}
						>
							{refundLoading ? 'Processing...' : 'Record Refund'}
						</Button>
					) : null}
				</CardContent>
			</Card>

			<CreateBidModal
				open={isCreateBidModalOpen}
				onClose={() => setIsCreateBidModalOpen(false)}
				jobId={jobId}
				onJobUpdated={onJobUpdated}
			/>

			<ChangeOrderModal
				open={isChangeOrderModalOpen}
				onClose={() => setIsChangeOrderModalOpen(false)}
				jobId={jobId}
				onJobUpdated={onJobUpdated}
			/>

			<SendInvoiceModal
				open={invoiceModalOpen}
				onClose={() => setInvoiceModalOpen(false)}
				bid={bid}
				onSend={handleSendInvoice}
				sending={invoiceLoading}
			/>

			<RecordRefundModal
				open={refundModalOpen}
				onClose={() => setRefundModalOpen(false)}
				job={selectedJob}
				bid={bid}
				onSubmit={handleRecordRefund}
				submitting={refundLoading}
			/>

			<Modal
				open={expirationModalOpen}
				onClose={() => setExpirationModalOpen(false)}
				modalTitle="New bid expiration"
				maxWidth="sm"
				alwaysMaxWidth
				useActionButtons
				actionButtons={
					<Box sx={{ display: 'flex', gap: 1 }}>
						<Button
							onClick={() => setExpirationModalOpen(false)}
							disabled={resendLoading}
						>
							Cancel
						</Button>
						<Button
							variant="contained"
							onClick={handleConfirmExpiration}
							disabled={resendLoading}
						>
							{resendLoading ? 'Sending...' : 'Save & send'}
						</Button>
					</Box>
				}
			>
				<Box sx={{ py: 1 }}>
					<TextField
						label="Proposal valid until"
						type="datetime-local"
						fullWidth
						value={expiresAtLocal}
						onChange={(e) => setExpiresAtLocal(e.target.value)}
						InputLabelProps={{ shrink: true }}
					/>
				</Box>
			</Modal>
		</Box>
	);
};

export default JobActions;
